import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { ColumnFiltersState, RowSelectionState } from "@tanstack/react-table";
import {
  Permissions,
  useAuthConfig,
  useIsCurrentPermission,
} from "@theprelude/core";
import { Button, ScheduleIcon, ToggleGroup, ToggleItem } from "@theprelude/ds";
import { useMemo, useState } from "react";
import z from "zod";
import { setPageTitle } from "@/components/app-shell/app-shell";
import { TestsTable } from "@/components/schedule/schedule-tests";
import { ThreatsTable } from "@/components/schedule/schedule-threats";
import { ToggleAutopilotButton } from "@/components/schedule/toggle-autopilot-button";
import { TogglePauseButton } from "@/components/schedule/toggle-pause-button";
import { scheduledColumn } from "@/components/table/columns";
import { useOverlay } from "@/hooks/use-overlay";
import { ensureAuth } from "@/lib/auth";
import { authorizeFeatures } from "@/lib/authorize-feature";
import { scheduleQueryOptions } from "@/queries/schedule";

const searchSchema = z.object({
  type: z.enum(["tests", "threats"]).optional().catch(undefined),
  status: z
    .enum(["scheduled", "unscheduled", "all"])
    .optional()
    .catch(undefined),
});

export const Route = createFileRoute("/_app/schedule")({
  beforeLoad: async (opts) => {
    setPageTitle("Schedule");
    await authorizeFeatures(opts.context, ["detect"]);

    return scheduleQueryOptions(
      await ensureAuth(opts.context.auth),
    ).ensureQuery(opts.context.queryClient);
  },
  validateSearch: searchSchema,
  component: SchedulePage,
});

function SchedulePage() {
  const authConfig = useAuthConfig();
  const search = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const scheduleQuery = scheduleQueryOptions(authConfig).useSuspenseQuery();
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});

  const status = search.status ?? "all";
  const type = search.type ?? "threats";

  const counts = scheduleQuery.data[type].count;

  const columnFilters: ColumnFiltersState = useMemo(
    () =>
      status === "all"
        ? []
        : [{ id: scheduledColumn.id, value: status === "scheduled" }],
    [status],
  );

  return (
    <div className="flex flex-1 flex-col overflow-hidden p-4 pb-0">
      <div className="flex flex-row justify-between gap-4 pb-5">
        <ToggleGroup
          value={type}
          onValueChange={(value) => {
            setRowSelection({});
            void navigate({ search: { type: value } });
          }}
        >
          <ToggleItem
            value="threats"
            count={scheduleQuery.data?.threats.count.all}
          >
            Threats
          </ToggleItem>
          <ToggleItem value="tests" count={scheduleQuery.data?.tests.count.all}>
            Tests
          </ToggleItem>
        </ToggleGroup>

        <ToggleGroup
          value={status}
          onValueChange={(value) => {
            setRowSelection({});
            void navigate({
              search: (prev) => ({ ...prev, status: value }),
            });
          }}
        >
          <ToggleItem value="all" count={counts?.all}>
            All
          </ToggleItem>
          <ToggleItem value="scheduled" count={counts?.scheduled}>
            Scheduled
          </ToggleItem>
          <ToggleItem value="unscheduled" count={counts?.unscheduled}>
            Not Scheduled
          </ToggleItem>
        </ToggleGroup>

        <AdminButtons />
      </div>

      {type === "threats" && (
        <ThreatsTable
          threats={scheduleQuery.data.threats.rows}
          columnFilters={columnFilters}
          rowSelection={rowSelection}
          setRowSelection={setRowSelection}
        />
      )}

      {type === "tests" && (
        <TestsTable
          tests={scheduleQuery.data.tests.rows}
          columnFilters={columnFilters}
          rowSelection={rowSelection}
          setRowSelection={setRowSelection}
        />
      )}
    </div>
  );
}

function AdminButtons() {
  const search = Route.useSearch();
  const type = search.type === "tests" ? "test" : "threat";
  const { data: isAdmin } = useIsCurrentPermission(Permissions.ADMIN);
  const overlay = useOverlay();

  return (
    <div className="flex flex-row gap-4">
      {isAdmin && (
        <>
          <Button
            intent="primary"
            icon={<ScheduleIcon />}
            onClick={() => {
              void overlay.open("schedule", { type });
            }}
          >
            Schedule
          </Button>
          <TogglePauseButton />
          <ToggleAutopilotButton />
        </>
      )}
    </div>
  );
}
